<!-- Template -->
<template>
  <!-- Suppression de l'en-tête spécifique ici, car géré par DefaultLayout/NavBar -->
  <div class="data-browser-view container mx-auto px-4 py-6">
    <!-- En-tête de la page -->
    <div class="mb-8">
      <h1 class="text-4xl font-bold text-bdc-black mb-3">Exploreur de graffitis</h1>
      <p class="text-lg text-gray-600">
        Découvrer la collection complète des graffitis<br />
        Les filtres et différents modes d'affichage sont là pour vous aider à naviguer.
      </p>
    </div>

    <GraffitiSubset
      :graffiti-list="allGraffiti"
      :loading="loading"
      :initial-view-mode="viewMode"
      :initial-filters="currentFilters"
      @update:filters="handleFiltersUpdate"
      @update:view="handleViewUpdate"
      @select-graffiti="selectGraffiti"
      @export="handleExport"
    />
  </div>
</template>

<script setup lang="ts">
import { useBreadcrumbsStore } from '@/stores/breadcrumbs'
import { useGraffitiStore } from '@/stores/graffiti'
import type { GraffitiWithThemes } from '@/types'
import GraffitiSubset from '@components/DataBrowser/GraffitiSubset.vue'
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// Type pour le mode de vue
type ViewMode = 'card' | 'list' | 'table'

// Type pour les filtres
interface Filters {
  sort: { by: string; direction: 'asc' | 'desc' }
  visible: boolean
  langues: string[]
  categories?: string[]
  thematiques?: string[]
  inedits?: boolean
  search: string
  type: 'all' | 'figure' | 'texte'
  mur: 'all' | 'Nord' | 'Sud'
  releves: string[]
}

// État
const loading = ref(true)
const allGraffiti = ref<GraffitiWithThemes[]>([])
const viewMode = ref<ViewMode>('card')
const currentFilters = ref<Filters>({
  sort: { by: 'idBdc', direction: 'asc' },
  visible: false,
  langues: [],
  search: '',
  type: 'all',
  mur: 'all',
  releves: [],
  inedits: false,
})

// Router et Store
const router = useRouter()
const route = useRoute()
const breadcrumbsStore = useBreadcrumbsStore()
const graffitiStore = useGraffitiStore()

// Clé pour sauvegarder la position de scroll dans sessionStorage
const SCROLL_POSITION_KEY = 'bdc-browser-scroll-position'

// Charger les données initiales
onMounted(async () => {
  // Réinitialiser les breadcrumbs
  breadcrumbsStore.initializeBreadcrumbs(route)

  loading.value = true

  try {
    // Utiliser le store Pinia au lieu de cacheService directement
    await graffitiStore.loadAllGraffitis()
    const graffitiWithThemes = graffitiStore.allGraffitis
    allGraffiti.value = graffitiWithThemes
  } catch (error) {
    console.error('[BdC-BrowserView] Erreur lors du chargement des graffitis:', error)
  } finally {
    loading.value = false
  }

  // Restaurer la position de scroll après le chargement
  // Utiliser plusieurs nextTick et un petit délai pour s'assurer que tout est rendu
  await nextTick()
  await nextTick()
  // Petit délai pour laisser le router et le layout finir leur rendu
  setTimeout(() => {
    restoreScrollPosition()
  }, 100)
})

// Watcher pour restaurer la position quand on revient à la page browse
watch(
  () => route.name,
  (newRouteName, oldRouteName) => {
    // Si on revient de la page détail vers browse, restaurer la position
    if (newRouteName === 'browse' && oldRouteName === 'graffiti-detail') {
      // Attendre que le DOM soit prêt
      nextTick(() => {
        setTimeout(() => {
          restoreScrollPosition()
        }, 150)
      })
    }
  }
)

// Sauvegarder la position de scroll
function saveScrollPosition() {
  const scrollPosition = window.scrollY || window.pageYOffset || document.documentElement.scrollTop
  sessionStorage.setItem(SCROLL_POSITION_KEY, scrollPosition.toString())
}

// Restaurer la position de scroll
function restoreScrollPosition() {
  const savedPosition = sessionStorage.getItem(SCROLL_POSITION_KEY)
  if (savedPosition) {
    const position = parseInt(savedPosition, 10)
    // Utiliser requestAnimationFrame pour s'assurer que le DOM est prêt
    requestAnimationFrame(() => {
      window.scrollTo({
        top: position,
        behavior: 'auto', // Pas de smooth scroll pour une restauration immédiate
      })
      // Nettoyer après restauration
      sessionStorage.removeItem(SCROLL_POSITION_KEY)
    })
  }
}

// Sélectionner un graffiti -> Naviguer vers la page détail
async function selectGraffiti(id: number) {
  // Sauvegarder la position de scroll avant de naviguer
  saveScrollPosition()
  router.push({ name: 'graffiti-detail', params: { id: id.toString() } })
}

// Handlers pour les événements du subset
function handleFiltersUpdate(newFilters: Filters) {
  currentFilters.value = { ...currentFilters.value, ...newFilters }
}

function handleViewUpdate(mode: ViewMode) {
  viewMode.value = mode
}

function handleExport() {
  // La logique d'export est maintenant gérée dans le subset
  console.log('[BdC-BrowserView] Export demandé')
}

// Nettoyer les requêtes d'images en cours quand on quitte la page
onUnmounted(() => {
  // Les composants LazyThumbnail gèrent déjà l'annulation via leur propre onUnmounted
  // Mais on peut aussi annuler toutes les requêtes du cache si nécessaire
  // thumbnailCache.cancelAllRequests() // Optionnel : annuler toutes les requêtes
})
</script>

<style scoped>
/* Styles pour les transitions du panneau latéral (peuvent être supprimés si le panneau n'est plus utilisé) */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
